import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Plus, Search, Trash2, Wrench, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  // Iter 128 — Debounce + AbortController para evitar race conditions cuando
  // el usuario escribe rápido (respuestas antiguas pisando resultados nuevos).
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const load = async (queryStr) => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const r = await api.get("/customers", {
        params: { q: queryStr ?? "" },
        signal: ctrl.signal,
      });
      setList(r.data);
    } catch (err) {
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        console.warn("[Customers] search error:", err);
      }
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q.trim()), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  const del = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar cliente "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Cliente eliminado");
      load(q.trim());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <header className="flex items-end justify-between pb-6 gap-4 flex-wrap border-b border-dashed border-[#D5D7CE]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Directorio
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">Clientes</h1>
        </div>
        <button
          onClick={() => navigate("/customers/new")}
          data-testid="customer-new"
          className="ds-btn primary"
        >
          <Plus size={15} /> Nuevo cliente
        </button>
      </header>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por código, nombre, NIF, teléfono, email"
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none pl-9 pr-3 py-2 text-sm rounded-none"
            data-testid="customer-search"
          />
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-[#525252]">
          {list.length} resultados
        </div>
      </div>

      <div className="mt-4 ds-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
              <th className="px-4 py-2.5 w-28">Nº cliente</th>
              <th className="px-4 py-2.5">Nombre</th>
              <th className="px-4 py-2.5">NIF/DNI</th>
              <th className="px-4 py-2.5">Teléfono</th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Población</th>
              <th className="px-4 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/customers/${c.id}`)}
                className="border-b border-[#E5E5E0] last:border-0 hover:bg-[#F7F7F4] cursor-pointer transition-colors"
                data-testid={`customer-row-${c.id}`}
              >
                <td className="px-4 py-2.5 font-mono text-xs">{c.code || "—"}</td>
                <td className="px-4 py-2.5 font-medium">{c.name}</td>
                <td className="px-4 py-2.5 text-[#525252] font-mono text-xs">{c.tax_id || "—"}</td>
                <td className="px-4 py-2.5 text-[#525252] font-mono text-xs">{c.phone || "—"}</td>
                <td className="px-4 py-2.5 text-[#525252]">{c.email || "—"}</td>
                <td className="px-4 py-2.5 text-[#525252]">{c.city || "—"}</td>
                <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/repairs/new?customer=${c.id}`); }}
                    className="p-1.5 hover:bg-[#F7F7F4] text-[#6E8B3D]"
                    title="Nueva reparación"
                    data-testid={`customer-newrepair-${c.id}`}
                  >
                    <Wrench size={14} />
                  </button>
                  <button
                    onClick={(e) => del(e, c)}
                    className="p-1.5 hover:bg-red-100 text-red-700"
                    data-testid={`customer-delete-${c.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.id}`); }}
                    className="p-1.5 hover:bg-[#E5E5E0]"
                    title="Abrir ficha"
                    data-testid={`customer-open-${c.id}`}
                  >
                    <ChevronRight size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-[#525252]">
                  No hay clientes aún. Crea uno con el botón "Nuevo cliente".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
